import React, {useMemo, useState, useRef, useEffect, useCallback} from 'react';

import {Button} from 'primereact/button';
import {InputNumber} from 'primereact/inputnumber';
import {Dialog} from 'primereact/dialog';
import {Dropdown} from 'primereact/dropdown';
import {Slider} from 'primereact/slider';
import {useDrillholeData} from "../components/hooks/useDrillholeData";
import {useSelector, useDispatch} from 'react-redux';
import {RootState} from '../redux/store';
import {Input} from "@dmt/global_npm";
import settings from '../config/settings';
import * as turf from '@turf/turf';

import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts/core';
import {
    GridComponent,
    TooltipComponent,
    DataZoomComponent,
    LegendComponent,
    AxisPointerComponent,
} from 'echarts/components';
import {LineChart} from 'echarts/charts';
import {CanvasRenderer} from 'echarts/renderers';
import TileViewer, {
    TileViewSyncState,
    TileViewerHandle,
    TileMetadata,
    TileLayerMeta,
    ViewSyncBus
} from "../components/coreViewer/TileViewer";

import {Extent} from "ol/extent";
import ControlBar from "./global/ControlBar";
import {useDrillholeDetails} from "../components/hooks/useDrillholeDetails";
import {useTemplates} from "../components/hooks/useTemplates";

import {DataSource, setCoreViewerDataSources, setCoreViewerActiveOverrides, setColorGradientXrf, setColorGradientMinerals, setColorByElementXrf, setColorByElementMinerals, GradientStop} from "../redux/slices/ChartStateSlice";
import {elementKey, unitSuffix} from "../functions/drillholeTransform";
import {exportCoreViewerPdf, buildSeriesData} from "../functions/pdfExport";

echarts.use([
    GridComponent,
    TooltipComponent,
    DataZoomComponent,
    LegendComponent,
    AxisPointerComponent,
    LineChart,
    CanvasRenderer,
]);

type SeriesPoint = [number, number];

const MAX_CHART_POINTS = 300;

// Fixed colour key for the "Mineral_Map" image layer. Shown as a legend under
// the Image Layer selector. The colour-name column from the source table is
// intentionally omitted — only the RGB swatch and mineral name are displayed.
const MINERAL_MAP_LEGEND: { mineral: string; rgb: [number, number, number] }[] = [
    {mineral: 'Hematite', rgb: [255, 0, 0]},
    {mineral: 'Goethite', rgb: [233, 113, 50]},
    {mineral: 'Maghemite', rgb: [241, 169, 131]},
    {mineral: 'Magnetite', rgb: [116, 116, 116]},
    {mineral: 'Siderite', rgb: [77, 147, 217]},
    {mineral: 'Actinolite', rgb: [142, 217, 115]},
    {mineral: 'Hornblende', rgb: [0, 176, 80]},
    {mineral: 'Chlorite', rgb: [0, 255, 0]},
    {mineral: 'Dolomite', rgb: [68, 179, 255]},
    {mineral: 'Calcite', rgb: [192, 230, 245]},
    {mineral: 'Gibbsite', rgb: [255, 255, 0]},
    {mineral: 'Kaolinite', rgb: [204, 204, 0]},
    {mineral: 'Jarosite', rgb: [120, 33, 112]},
    {mineral: 'Muscovite', rgb: [51, 153, 51]},
    {mineral: 'Biotite', rgb: [255, 51, 204]},
    {mineral: 'Phlogopite', rgb: [0, 32, 96]},
    {mineral: 'Quartz', rgb: [255, 255, 153]},
    {mineral: 'Nontronite', rgb: [247, 61, 185]},
    {mineral: 'Beidellite', rgb: [247, 61, 185]},
    {mineral: 'Saponite', rgb: [216, 109, 205]},
    {mineral: 'Pyrophyllite', rgb: [126, 53, 14]},
    {mineral: 'Montmorillonite', rgb: [153, 153, 255]},
    {mineral: 'Talc', rgb: [218, 242, 218]},
    {mineral: 'Illite', rgb: [102, 102, 51]},
    {mineral: 'Unknown', rgb: [0, 0, 0]},
];

/**
 * Downsample a sorted series using a min/max approach per bin.
 * For each depth-bin we keep the point with the highest value and the one
 * with the lowest value so that peaks and valleys are never averaged away.
 * The global maximum point of the *entire* series is always preserved so
 * that the user can always see it, even when zoomed out.
 */
function downsampleByDepth(
    data: SeriesPoint[],
    minDepth: number,
    maxDepth: number,
    maxPoints: number = MAX_CHART_POINTS,
): SeriesPoint[] {
    if (data.length <= maxPoints) return data;

    const range = maxDepth - minDepth;
    const padding = range * 0.15;
    const lo = minDepth - padding;
    const hi = maxDepth + padding;

    // Filter to visible range (data is sorted by depth in [1])
    let startIdx = 0;
    let endIdx = data.length;
    for (let i = 0; i < data.length; i++) {
        if (data[i][1] >= lo) { startIdx = i; break; }
    }
    for (let i = data.length - 1; i >= 0; i--) {
        if (data[i][1] <= hi) { endIdx = i + 1; break; }
    }
    const visible = data.slice(startIdx, endIdx);
    if (visible.length <= maxPoints) return visible;

    // Find global max of the full series (always preserved)
    let globalMaxIdx = 0;
    for (let i = 1; i < data.length; i++) {
        if (data[i][0] > data[globalMaxIdx][0]) globalMaxIdx = i;
    }
    const globalMaxPoint = data[globalMaxIdx];

    // Bin by depth — keep min and max value per bin
    const halfPoints = Math.floor(maxPoints / 2);
    const binSize = (hi - lo) / halfPoints;
    const bins: Array<{ min: SeriesPoint; max: SeriesPoint }> = [];
    const binMap = new Map<number, number>();

    for (let i = 0; i < visible.length; i++) {
        const pt = visible[i];
        const bi = Math.floor((pt[1] - lo) / binSize);
        const idx = binMap.get(bi);
        if (idx !== undefined) {
            if (pt[0] < bins[idx].min[0]) bins[idx].min = pt;
            if (pt[0] > bins[idx].max[0]) bins[idx].max = pt;
        } else {
            binMap.set(bi, bins.length);
            bins.push({ min: pt, max: pt });
        }
    }

    // Collect unique points (min and max may be the same point)
    const seen = new Set<number>();
    const result: SeriesPoint[] = [];
    for (let i = 0; i < bins.length; i++) {
        const depthMin = bins[i].min[1];
        const depthMax = bins[i].max[1];
        if (!seen.has(depthMin)) { seen.add(depthMin); result.push(bins[i].min); }
        if (!seen.has(depthMax)) { seen.add(depthMax); result.push(bins[i].max); }
    }

    // Ensure global max point is always included
    if (!seen.has(globalMaxPoint[1])) {
        result.push(globalMaxPoint);
    }

    result.sort((a, b) => a[1] - b[1]);
    return result;
}

const DrillholeRow = React.memo(function DrillholeRow({
                                                          drillhole,
                                                          projectId,
                                                          elements,
                                                          totalCount,
                                                          syncView,
                                                          onViewChange,
                                                          viewSyncBus,
                                                          sharedScrollLeft,
                                                          onScrollChange,
                                                          activeLayer,
                                                          tileSplitterSizes,
                                                          onTileSplitterResize,
                                                          onManualSplitterAdjust,
                                                          xAxisMax,
                                                          xAxisMin,
                                                          elementMaxValues,
                                                          elementMinValues,
                                                          chartMaxValues,
                                                          chartMinValues,
                                                          chartGroupMaxValues,
                                                          combinedChartCount,
                                                          elementChartIndex,
                                                          layoutDirection,
                                                          combinedChart,
                                                          showMarkers,
                                                          isDark,
                                                          onTilesGenerated,
                                                          onViewerReady,
                                                          onExtentsChange,
                                                          viewExtents,
                                                          dataSources,
                                                          showDepthScale,
                                                          chartFixedWidth,
                                                          titleSize,
                                                      }: {
    drillhole: any;
    projectId: string;
    elements: any[];
    totalCount: number;
    syncView: TileViewSyncState | null;
    onViewChange: (view: TileViewSyncState) => void;
    viewSyncBus?: ViewSyncBus;
    sharedScrollLeft: number;
    onScrollChange: (scrollLeft: number) => void;
    activeLayer: string;
    tileSplitterSizes: number[];
    onTileSplitterResize: (sizes: number[]) => void;
    onManualSplitterAdjust?: () => void;
    xAxisMax: number;
    xAxisMin: number;
    elementMaxValues: Record<string, number>;
    elementMinValues: Record<string, number>;
    chartMaxValues: Record<number, number>;
    chartMinValues: Record<number, number>;
    chartGroupMaxValues: number[];
    combinedChartCount: number;
    elementChartIndex: Record<string, number>;
    layoutDirection: string;
    combinedChart: boolean;
    showMarkers: boolean;
    isDark: boolean;
    onTilesGenerated?: () => void;
    onViewerReady?: (holeId: string, handle: TileViewerHandle) => void;
    onExtentsChange?: (min: number, max: number) => void;
    viewExtents?: [number, number] | null;
    dataSources: string[];
    showDepthScale: boolean;
    chartFixedWidth: number | null;
    titleSize: number;
}) {
    const [chartOptions, setChartOptions] = useState<any>(null);
    const [yAxisChartOptions, setYAxisChartOptions] = useState<any>(null);
    const [chartWidth, setChartWidth] = useState<number>(0);
    const [chartAreaWidthPx, setChartAreaWidthPx] = useState<number>(0);
    const chartAreaRef = useRef<HTMLDivElement | null>(null);
    const chartScrollRef = useRef<HTMLDivElement | null>(null);
    const tileViewerHandleRef = useRef<TileViewerHandle | null>(null);
    // Admin-only controls (e.g. force tile regeneration) require ?admin=true in the URL.
    const isAdmin = useMemo(() => new URLSearchParams(window.location.search).get('admin') === 'true', []);
    const isSyncingScrollRef = useRef(false);
    const echartsRef = useRef<echarts.EChartsType | null>(null);
    const yAxisEchartsRef = useRef<echarts.EChartsType | null>(null);
    const hoveredPointRef = useRef<{seriesIndex: number} | null>(null);
    // Update chart yAxis directly — silent + lazyUpdate to minimize overhead
    const lastExtentsRef = useRef<[number, number]>([0, 0]);
    const splitContainerRef = useRef<HTMLDivElement>(null);
    const tileSplitterSizesRef = useRef(tileSplitterSizes);
    tileSplitterSizesRef.current = tileSplitterSizes;
    const handleGutterMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        const container = splitContainerRef.current;
        if (!container) return;
        onManualSplitterAdjust?.();
        const startX = e.clientX;
        const containerWidth = container.getBoundingClientRect().width;
        const startPercent = tileSplitterSizesRef.current[0];
        const onMouseMove = (ev: MouseEvent) => {
            const delta = ev.clientX - startX;
            const newPercent = Math.max(10, Math.min(80, startPercent + (delta / containerWidth) * 100));
            onTileSplitterResize([newPercent, 100 - newPercent]);
        };
        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }, [onTileSplitterResize, onManualSplitterAdjust]);
    const handleExtentsChange = useCallback((extents: Extent | null) => {
        if (!extents) return;
        // TileViewer sends negative depths [0, -depthBottom, 1, -depthTop],
        // but chart data uses positive depths — negate and swap
        const min = -extents[3];
        const max = -extents[1];
        if (Math.abs(min - lastExtentsRef.current[0]) < 0.01
            && Math.abs(max - lastExtentsRef.current[1]) < 0.01) return;
        lastExtentsRef.current = [min, max];
        onExtentsChange?.(min, max);

        if (echartsRef.current) {
            const axisCount = combinedChart ? combinedChartCount : elements.length;
            echartsRef.current.setOption({
                yAxis: Array.from({length: axisCount}, (_, i) => ({
                    gridIndex: i,
                    min,
                    max,
                })),
            }, {notMerge: false, lazyUpdate: true, silent: true});
        }

        yAxisEchartsRef.current?.setOption({
            yAxis: [{gridIndex: 0, min, max}],
        }, {notMerge: false, lazyUpdate: true, silent: true});
    }, [elements.length, combinedChart, combinedChartCount, onExtentsChange]);

    // Sync scrollLeft von anderen Rows empfangen
    useEffect(() => {
        if (!chartScrollRef.current) return;
        if (Math.abs(chartScrollRef.current.scrollLeft - sharedScrollLeft) < 1) return;
        isSyncingScrollRef.current = true;
        chartScrollRef.current.scrollLeft = sharedScrollLeft;
        requestAnimationFrame(() => {
            isSyncingScrollRef.current = false;
        });
    }, [sharedScrollLeft]);

    // Track chart area pixel width so multi-chart titles can wrap to fit
    useEffect(() => {
        const el = chartAreaRef.current;
        if (!el) return;
        let rafId = 0;
        const update = () => {
            const w = el.getBoundingClientRect().width;
            if (w > 0) setChartAreaWidthPx((prev) => Math.abs(prev - w) > 1 ? w : prev);
        };
        update();
        const ro = new ResizeObserver(() => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(update);
        });
        ro.observe(el);
        return () => {
            cancelAnimationFrame(rafId);
            ro.disconnect();
        };
    }, []);

    const buildSeries = (dataSource: SeriesPoint[][], element: any, index: number, singleGrid: boolean, chartGroupIndex: number, showMarkers: boolean) => {
        return {
            id: elementKey(element),
            name: element.name,
            type: 'line' as const,
            showSymbol: showMarkers,
            smooth: false,
            large: true,
            symbolSize: 6,
            xAxisIndex: singleGrid ? chartGroupIndex : index,
            yAxisIndex: singleGrid ? chartGroupIndex : index,
            lineStyle: {width: 1.5},
            itemStyle: {color: element.color},
            areaStyle: undefined,
            data: dataSource[index],
        };
    };

    const all = useMemo(() => {
        if (!drillhole || !elements) return [];

        // Build series from both XRF (dri_mea) and Minerals (dri_mea_minerals)
        // — each element's `source` decides which measurement array to read.
        const result: SeriesPoint[][] = elements.map(() => []);

        const fillFromSource = (measurements: any[] | undefined, source: 'xrf' | 'minerals') => {
            if (!measurements) return;
            const indexMap = new Map<number, number>();
            elements.forEach((el: any, idx: number) => {
                if ((el.source ?? 'xrf') === source) indexMap.set(el.id, idx);
            });
            if (indexMap.size === 0) return;
            for (let i = 0; i < measurements.length; i++) {
                const mea = measurements[i];
                for (let j = 0; j < (mea.mea_xrfres?.length ?? 0); j++) {
                    const xrf = mea.mea_xrfres[j];
                    for (let k = 0; k < (xrf.xrfres_spe?.length ?? 0); k++) {
                        const spe = xrf.xrfres_spe[k];
                        const depth = spe.position * -1;
                        for (let l = 0; l < (spe.spe_speres?.length ?? 0); l++) {
                            const speres = spe.spe_speres[l];
                            const idx = indexMap.get(speres.element_id);
                            if (idx !== undefined) {
                                result[idx].push([speres.value, depth]);
                            }
                        }
                    }
                }
            }
        };

        fillFromSource(drillhole.dri_mea, 'xrf');
        fillFromSource(drillhole.dri_mea_minerals, 'minerals');

        // Sort each series by depth (y-value) for correct line rendering
        for (let i = 0; i < result.length; i++) {
            result[i].sort((a, b) => a[1] - b[1]);
        }

        return result;
    }, [drillhole, elements]);

    // Downsampled version — adapts to current visible depth range for higher
    // resolution when zoomed in, falls back to full range when zoomed out.
    const downsampled = useMemo(() => {
        return all.map((seriesData) => {
            if (seriesData.length <= MAX_CHART_POINTS) return seriesData;
            const fullMinD = seriesData[0]?.[1] ?? 0;
            const fullMaxD = seriesData[seriesData.length - 1]?.[1] ?? 0;
            const minD = viewExtents ? viewExtents[0] : fullMinD;
            const maxD = viewExtents ? viewExtents[1] : fullMaxD;
            return downsampleByDepth(seriesData, minD, maxD, MAX_CHART_POINTS);
        });
    }, [all, viewExtents]);

    const initialDepthRange = useMemo(() => {
        let min = Infinity, max = -Infinity;
        for (const s of all) {
            for (const p of s) {
                if (p[1] < min) min = p[1];
                if (p[1] > max) max = p[1];
            }
        }
        return min === Infinity ? [0, 100] : [min, max];
    }, [all]);

    const series = useMemo(() => {
        return elements.map((ele: any, index: number) => {
            const chartGroupIndex = combinedChart
                ? Math.min((elementChartIndex[elementKey(ele)] ?? 1) - 1, combinedChartCount - 1)
                : index;
            return buildSeries(downsampled, ele, index, combinedChart, chartGroupIndex, showMarkers);
        });
    }, [downsampled, elements, combinedChart, elementChartIndex, combinedChartCount, showMarkers]);

    useEffect(() => {
        const labelColor = isDark ? '#ffffff' : '#333';
        const isFixed = chartFixedWidth != null && chartFixedWidth > 0;

        // Series id (== elementKey) -> unit, so tooltips can show the unit after the value.
        const unitBySeriesId: Record<string, string> = {};
        elements.forEach((el: any) => { unitBySeriesId[elementKey(el)] = el.unit ?? ''; });

        if (combinedChart) {
            const count = Math.max(1, combinedChartCount);
            const isSingle = count === 1;

            const gridLeftPx = 10;
            const gridRightPx = 30;
            const gridGapPx = 10;
            // Map 1..10 logical units to font sizes / vertical padding so the
            // grid top adjusts smoothly. Default 5 reproduces the original 11px
            // titles and 40/60px grid-top padding.
            const ts = Math.max(1, Math.min(10, titleSize || 5));
            const nameFontSize = 7 + ts;            // 8..17px
            const dotFontSize = nameFontSize + 2;
            const lineHeight = nameFontSize + 4;
            const titleGridTopPx = isSingle ? Math.round(35 + ts * 1) : Math.round(50 + ts * 2);
            const MAX_NAMES_IN_TITLE = 5;

            const perChartPxEstimate = isFixed
                ? (chartFixedWidth as number)
                : (chartAreaWidthPx > 0
                    ? Math.max(40, (chartAreaWidthPx - gridLeftPx - gridRightPx - (count - 1) * gridGapPx) / count)
                    : 200);

            let grids: any[];
            let chartTitles: any[];
            let containerWidthPx: number;

            const buildTitles = (titleLeft: (i: number) => number | string) => (
                isSingle ? [] : Array.from({length: count}, (_, i) => {
                    const matched = elements.filter((el: any) =>
                        Math.min((elementChartIndex[elementKey(el)] ?? 1) - 1, count - 1) === i
                    );
                    const displayed = matched.slice(0, MAX_NAMES_IN_TITLE);
                    const extra = Math.max(0, matched.length - MAX_NAMES_IN_TITLE);

                    const rich: Record<string, any> = {
                        prefix: {fill: labelColor, fontSize: nameFontSize, padding: [0, 6, 0, 0]},
                        more: {fill: labelColor, fontSize: nameFontSize, padding: [0, 0, 0, 4]},
                    };
                    const parts: string[] = [`{prefix|Chart ${i + 1}${displayed.length ? ':' : ''}}`];
                    displayed.forEach((el: any, idx: number) => {
                        const dotKey = `d${idx}`;
                        const nameKey = `n${idx}`;
                        rich[dotKey] = {fill: el.color, fontSize: dotFontSize};
                        rich[nameKey] = {fill: labelColor, fontSize: nameFontSize, padding: [0, 8, 0, 4]};
                        parts.push(`{${dotKey}|●}{${nameKey}|${el.name}}`);
                    });
                    if (extra > 0) parts.push(`{more|+${extra}}`);

                    return {
                        type: 'text',
                        left: titleLeft(i),
                        top: 6,
                        style: {
                            text: parts.join(''),
                            font: `${nameFontSize}px Arial`,
                            textAlign: 'left',
                            fill: labelColor,
                            width: Math.max(40, perChartPxEstimate - 8),
                            overflow: 'break',
                            lineHeight: lineHeight,
                            rich,
                        },
                    };
                })
            );

            if (isFixed) {
                const w = chartFixedWidth as number;
                grids = isSingle
                    ? [{left: gridLeftPx, width: w, top: titleGridTopPx, bottom: 60}]
                    : Array.from({length: count}, (_, i) => ({
                        left: gridLeftPx + i * (w + gridGapPx),
                        width: w,
                        top: titleGridTopPx,
                        bottom: 60,
                    }));
                chartTitles = buildTitles((i) => gridLeftPx + i * (w + gridGapPx));
                containerWidthPx = gridLeftPx + count * w + (count - 1) * gridGapPx + gridRightPx;
            } else {
                const leftPct = 2;
                const rightPct = 2;
                const gapPct = 2;
                const chartWidthPct = (100 - leftPct - rightPct - gapPct * (count - 1)) / count;

                grids = isSingle
                    ? [{left: gridLeftPx, right: gridRightPx, top: titleGridTopPx, bottom: 60}]
                    : Array.from({length: count}, (_, i) => ({
                        left: `${(leftPct + i * (chartWidthPct + gapPct)).toFixed(2)}%`,
                        width: `${chartWidthPct.toFixed(2)}%`,
                        top: titleGridTopPx,
                        bottom: 60,
                    }));
                chartTitles = buildTitles((i) => `${(leftPct + i * (chartWidthPct + gapPct)).toFixed(2)}%`);
                containerWidthPx = 0;
            }

            const xAxes = Array.from({length: count}, (_, i) => {
                // Show the unit on the axis when every element in this chart shares it.
                const chartUnits = Array.from(new Set(
                    elements
                        .filter((el: any) => Math.min((elementChartIndex[elementKey(el)] ?? 1) - 1, count - 1) === i)
                        .map((el: any) => el.unit)
                        .filter(Boolean)
                ));
                const axisUnit = chartUnits.length === 1 ? (chartUnits[0] as string) : '';
                return {
                    type: 'value' as const,
                    gridIndex: i,
                    name: axisUnit,
                    nameLocation: 'middle' as const,
                    nameGap: 30,
                    nameTextStyle: {color: labelColor, fontSize: 11},
                    axisLine: {onZero: false},
                    splitLine: {show: true},
                    min: chartMinValues[i] ?? xAxisMin,
                    max: (chartMaxValues[i] ?? (count > 1 ? chartGroupMaxValues[i] : xAxisMax)) || 1,
                    axisLabel: {
                        hideOverlap: true,
                        color: labelColor,
                        formatter: (v: number) => (+v.toFixed(1)).toString().replace('.', ','),
                    },
                };
            });

            const yAxes = Array.from({length: count}, (_, i) => ({
                type: 'value' as const,
                gridIndex: i,
                inverse: true,
                boundaryGap: false,
                min: lastExtentsRef.current[0] !== 0 || lastExtentsRef.current[1] !== 0 ? lastExtentsRef.current[0] : initialDepthRange[0],
                max: lastExtentsRef.current[0] !== 0 || lastExtentsRef.current[1] !== 0 ? lastExtentsRef.current[1] : initialDepthRange[1],
                axisLine: {show: false, onZero: false},
                axisTick: {show: false},
                axisLabel: {show: false},
                splitLine: {show: true},
                minorTick: {show: false},
                minorSplitLine: {show: false},
            }));

            setChartOptions({
                animation: false,
                progressive: 200,
                progressiveThreshold: 400,
                legend: {show: false},
                tooltip: {
                    trigger: 'axis',
                    confine: true,
                    throttle: 80,
                    axisPointer: {type: 'line', axis: 'y', snap: false},
                    formatter: (params: any) => {
                        const list = (Array.isArray(params) ? params : [params]) as any[];
                        if (list.length === 0) return '';
                        const hovered = hoveredPointRef.current;
                        const display = hovered
                            ? list.filter((p: any) => p.seriesIndex === hovered.seriesIndex)
                            : list;
                        const source = display.length > 0 ? display : list;
                        const depth = Array.isArray(source[0].value) ? source[0].value[1] : undefined;
                        const depthStr = depth !== undefined ? `Depth: ${Math.round(depth)}m` : '';
                        const lines = source.map((p: any) => {
                            const val = Array.isArray(p.value) ? p.value[0] : p.value;
                            return `${p.marker}${p.seriesName}: ${(+val).toFixed(2).replace('.', ',')}${unitSuffix(unitBySeriesId[p.seriesId])}`;
                        });
                        return [...lines, depthStr].join('<br/>');
                    },
                },
                graphic: chartTitles,
                grid: grids,
                xAxis: xAxes,
                yAxis: yAxes,
                series: series,
            });

            setChartWidth(containerWidthPx);
        } else {
            const gridLeftPx = 10;
            const gridGapPx = 10;
            const count = Math.max(1, elements.length);

            // Match the title scale used by the combined-chart branch.
            const ts = Math.max(1, Math.min(10, titleSize || 5));
            const nameFontSize = 8 + ts;             // 9..18px
            const subFontSize = Math.max(8, nameFontSize - 1);
            const dotFontSize = nameFontSize + 2;
            const lineHeight = nameFontSize + 4;
            const singleTitleTopPx = Math.round(35 + ts * 2);

            let grids: any[];
            let chartTitles: any[];
            let containerWidthPx: number;

            const buildTitles = (titleLeft: (i: number) => number | string) => (
                elements.map((element: any, i: number) => ({
                    type: 'text',
                    left: titleLeft(i),
                    top: 10,
                    style: {
                        text: `{dot|●}{name|${element.name}}\n{sub|${element.shell}${element.sub_shell}}`,
                        font: `${nameFontSize}px Arial`,
                        textAlign: 'left',
                        fill: labelColor,
                        lineHeight: lineHeight,
                        rich: {
                            dot: {fill: element.color, fontSize: dotFontSize},
                            name: {fill: labelColor, fontSize: nameFontSize, padding: [0, 0, 0, 4]},
                            sub: {fill: labelColor, fontSize: subFontSize, padding: [0, 0, 0, 12]},
                        },
                    },
                }))
            );

            if (isFixed) {
                const w = chartFixedWidth as number;
                grids = elements.map((_: any, i: number) => ({
                    left: gridLeftPx + i * (w + gridGapPx),
                    width: w,
                    top: singleTitleTopPx,
                    bottom: 60,
                }));
                chartTitles = buildTitles((i) => gridLeftPx + i * (w + gridGapPx));
                containerWidthPx = gridLeftPx + count * w + Math.max(0, count - 1) * gridGapPx + 60;
            } else {
                const leftPct = 2;
                const rightPct = 2;
                const gapPct = 2;
                const chartWidthPct = (100 - leftPct - rightPct - gapPct * (count - 1)) / count;
                grids = elements.map((_: any, i: number) => ({
                    left: `${(leftPct + i * (chartWidthPct + gapPct)).toFixed(2)}%`,
                    width: `${chartWidthPct.toFixed(2)}%`,
                    top: singleTitleTopPx,
                    bottom: 60,
                }));
                chartTitles = buildTitles((i) => `${(leftPct + i * (chartWidthPct + gapPct)).toFixed(2)}%`);
                containerWidthPx = 0;
            }

            const xAxes = elements.map((element: any, i: number) => ({
                type: 'value' as const,
                gridIndex: i,
                name: element.unit ?? '',
                nameLocation: 'middle' as const,
                nameGap: 30,
                nameTextStyle: {color: labelColor, fontSize: 11},
                axisLine: {onZero: false},
                splitLine: {show: true},
                min: elementMinValues[elementKey(element)] ?? xAxisMin,
                max: elementMaxValues[elementKey(element)] ?? (xAxisMax || 1),
                axisLabel: {
                    hideOverlap: true,
                    color: labelColor,
                    formatter: (v: number) => (+v.toFixed(1)).toString().replace('.', ','),
                }
            }));

            const yAxes = elements.map((_: any, i: number) => ({
                type: 'value' as const,
                inverse: true,
                boundaryGap: false,
                min: lastExtentsRef.current[0] !== 0 || lastExtentsRef.current[1] !== 0 ? lastExtentsRef.current[0] : initialDepthRange[0],
                max: lastExtentsRef.current[0] !== 0 || lastExtentsRef.current[1] !== 0 ? lastExtentsRef.current[1] : initialDepthRange[1],
                axisLine: {show: false, onZero: false},
                axisTick: {show: false},
                axisLabel: {show: false},
                splitLine: {show: true},
                minorTick: {show: false},
                minorSplitLine: {show: false},
            }));

            setChartOptions({
                animation: false,
                progressive: 200,
                progressiveThreshold: 400,
                legend: {show: false},
                tooltip: {
                    trigger: 'item',
                    confine: true,
                    throttle: 80,
                    formatter: (params: any) => {
                        const p = Array.isArray(params) ? params[0] : params;
                        if (!p) return '';
                        const val = Array.isArray(p.value) ? p.value[0] : p.value;
                        const depth = Array.isArray(p.value) ? p.value[1] : undefined;
                        const depthStr = depth !== undefined ? `Depth: ${Math.round(depth)}m` : '';
                        const valStr = `${p.marker}${p.seriesName}: ${(+val).toFixed(2).replace('.', ',')}${unitSuffix(unitBySeriesId[p.seriesId])}`;
                        return depthStr ? `${valStr}<br/>${depthStr}` : valStr;
                    },
                },
                graphic: chartTitles,
                grid: grids,
                xAxis: xAxes.map((cfg: any, i: number) => ({...cfg, gridIndex: i})),
                yAxis: yAxes.map((cfg: any, i: number) => ({...cfg, gridIndex: i})),
                series: series,
            });

            setChartWidth(containerWidthPx);
        }

        // Sticky left yAxis chart (depth scale) — kept in sync via handleExtentsChange
        setYAxisChartOptions({
            animation: false,
            grid: [{left: 38, right: 0, top: 40, bottom: 60}],
            xAxis: [{type: 'value', show: false, min: 0, max: 1, gridIndex: 0}],
            yAxis: [{
                type: 'value',
                inverse: true,
                boundaryGap: false,
                min: lastExtentsRef.current[0] !== 0 || lastExtentsRef.current[1] !== 0 ? lastExtentsRef.current[0] : initialDepthRange[0],
                max: lastExtentsRef.current[0] !== 0 || lastExtentsRef.current[1] !== 0 ? lastExtentsRef.current[1] : initialDepthRange[1],
                axisLine: {show: true, onZero: false},
                axisTick: {show: true},
                axisLabel: {
                    color: labelColor,
                    formatter: (v: number) => Math.round(v).toString(),
                },
                splitLine: {show: false},
                minorTick: {show: false},
                minorSplitLine: {show: false},
            }],
            series: [],
        });

    }, [drillhole, all, series, elements, xAxisMax, xAxisMin, elementMaxValues, elementMinValues, chartMaxValues, chartMinValues, chartGroupMaxValues, combinedChart, combinedChartCount, elementChartIndex, isDark, initialDepthRange, chartFixedWidth, chartAreaWidthPx, titleSize]);

    const sizeStyle = layoutDirection === 'horizontal'
        ? (totalCount <= 3
            ? {flex: `1 1 0`, minWidth: 0, height: '100%'}
            : {flex: `0 0 33.33%`, minWidth: 0, height: '100%'})
        : (totalCount <= 3
            ? {flex: `1 1 0`, minHeight: 0}
            : {flex: `0 0 33.33%`, minHeight: 0});

    const hasCharts = dataSources.length > 0 && elements.length > 0;

    const tileViewerBlock = (
        <div className='card w-full mb-0'
             style={{overflow: 'hidden', flex: '1 1 0', minHeight: 0, paddingTop: 40, paddingBottom: 60}}>
            <TileViewer name={drillhole.hole_id} projectId={projectId} setExtents={handleExtentsChange} syncView={syncView}
                        onViewChange={onViewChange} viewSyncBus={viewSyncBus} onReady={(handle) => {
                tileViewerHandleRef.current = handle;
                onViewerReady?.(drillhole.hole_id, handle);
            }} activeLayer={activeLayer} onTilesGenerated={onTilesGenerated}/>
        </div>
    );

    const chartBlock = (
        <div ref={chartScrollRef} className='card h-full mb-0'
             style={{overflowX: 'auto', overflowY: 'hidden', paddingTop: 0, paddingBottom: 0, display: 'flex'}}
             onWheel={(e) => {
                 if (Math.abs(e.deltaX) > Math.abs(e.deltaY) && chartScrollRef.current) {
                     e.stopPropagation();
                     chartScrollRef.current.scrollLeft += e.deltaX;
                     onScrollChange(chartScrollRef.current.scrollLeft);
                 } else if (Math.abs(e.deltaY) > 0 && tileViewerHandleRef.current) {
                     e.stopPropagation();
                     e.preventDefault();
                     tileViewerHandleRef.current.panBy(0, e.deltaY);
                 }
             }}
             onScroll={() => {
                 if (!isSyncingScrollRef.current && chartScrollRef.current) {
                     onScrollChange(chartScrollRef.current.scrollLeft);
                 }
             }}>
            {chartOptions && <>
                <div style={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 10,
                    flexShrink: 0,
                    width: 50,
                    height: '100%',
                    background: isDark ? '#1e1e1e' : '#ffffff',
                }}>
                    {yAxisChartOptions && <ReactECharts
                        option={yAxisChartOptions}
                        style={{height: '100%', width: '50px'}}
                        notMerge={false}
                        lazyUpdate
                        onChartReady={(inst) => {
                            yAxisEchartsRef.current = inst;
                            if (lastExtentsRef.current[0] !== 0 || lastExtentsRef.current[1] !== 0) {
                                inst.setOption({
                                    yAxis: [{
                                        gridIndex: 0,
                                        min: lastExtentsRef.current[0],
                                        max: lastExtentsRef.current[1],
                                    }],
                                }, {notMerge: false, lazyUpdate: true, silent: true});
                            }
                        }}
                    />}
                </div>
                <div ref={chartAreaRef} style={{
                    flexShrink: 0,
                    width: chartWidth > 0 ? `${chartWidth}px` : 'calc(100% - 50px)',
                    height: '100%',
                }}>
                    <ReactECharts
                        option={chartOptions}
                        style={{height: '100%', width: '100%'}}
                        notMerge
                        lazyUpdate
                        onChartReady={(inst) => {
                            echartsRef.current = inst;
                            if (lastExtentsRef.current[0] !== 0 || lastExtentsRef.current[1] !== 0) {
                                const axisCount = combinedChart ? combinedChartCount : elements.length;
                                inst.setOption({
                                    yAxis: Array.from({length: axisCount}, (_, i) => ({
                                        gridIndex: i,
                                        min: lastExtentsRef.current[0],
                                        max: lastExtentsRef.current[1],
                                    })),
                                }, {notMerge: false, lazyUpdate: true, silent: true});
                            }
                            inst.on('mouseover', 'series', (e: any) => {
                                hoveredPointRef.current = {seriesIndex: e.seriesIndex};
                            });
                            inst.on('mouseout', 'series', () => {
                                hoveredPointRef.current = null;
                            });
                        }}
                    />
                </div>
            </>}
        </div>
    );

    const depthScaleBlock = showDepthScale && yAxisChartOptions ? (
        <div style={{flexShrink: 0, width: 50, height: '100%'}}>
            <ReactECharts
                option={yAxisChartOptions}
                style={{height: '100%', width: '50px'}}
                notMerge={false}
                lazyUpdate
                onChartReady={(inst) => {
                    yAxisEchartsRef.current = inst;
                    if (lastExtentsRef.current[0] !== 0 || lastExtentsRef.current[1] !== 0) {
                        inst.setOption({
                            yAxis: [{gridIndex: 0, min: lastExtentsRef.current[0], max: lastExtentsRef.current[1]}],
                        }, {notMerge: false, lazyUpdate: true, silent: true});
                    }
                }}
            />
        </div>
    ) : null;

    return (
        <div style={{...sizeStyle, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0, margin: '0 0 0.25rem 0'}}>
                <h4 style={{margin: 0, marginLeft: '1rem'}}>{drillhole.hole_id}</h4>
                {isAdmin && (
                    <Button
                        icon="pi pi-refresh"
                        rounded
                        text
                        severity="secondary"
                        tooltip="Regenerate tiles (rebuild from scratch)"
                        tooltipOptions={{position: 'bottom'}}
                        onClick={() => {
                            if (window.confirm(`Discard cached tiles for ${drillhole.hole_id} and rebuild from scratch?`)) {
                                tileViewerHandleRef.current?.regenerateTiles();
                            }
                        }}
                    />
                )}
            </div>
            {hasCharts ? (
                <div ref={splitContainerRef} style={{flex: '1 1 0', minHeight: 0, display: 'flex', overflow: 'hidden'}}>
                    <div style={{width: `${tileSplitterSizes[0]}%`, flexShrink: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
                        {tileViewerBlock}
                    </div>
                    <div
                        style={{width: '4px', cursor: 'col-resize', background: 'var(--surface-border)', flexShrink: 0, userSelect: 'none'}}
                        onMouseDown={handleGutterMouseDown}
                    />
                    <div style={{flex: '1 1 0', overflow: 'hidden', minWidth: 0}}>
                        {chartBlock}
                    </div>
                </div>
            ) : (
                <div style={{flex: '1 1 0', minHeight: 0, display: 'flex', overflow: 'hidden'}}>
                    {depthScaleBlock}
                    <div style={{flex: '1 1 0', minWidth: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
                        {tileViewerBlock}
                    </div>
                </div>
            )}
        </div>
    );
});

export default function CoreViewer() {
    const dispatch = useDispatch();
    const {data, withCoords} = useDrillholeData();
    const selectLine = useSelector((state: any) => state.oldDesignState.selectLine);
    const mapBufferOffset = useSelector((state: any) => state.oldDesignState.offset);
    const projectId = useSelector((state: RootState) => state.project.selectedProject?.project_id);
    const xrfElements = useSelector((state: RootState) => state.oldDesignState.xrfElements);
    const mineralsElements = useSelector((state: RootState) => state.oldDesignState.mineralsElements);
    const selectedBoreholesRaw = useSelector((state: RootState) => state.oldDesignState.selectedBoreholes);
    const dataSources = useSelector((state: RootState) => state.oldDesignState.coreViewerDataSources) as DataSource[];
    const activeOverrides = useSelector((state: RootState) => state.oldDesignState.coreViewerActiveOverrides) as Record<string, boolean>;
    const colorGradientXrf = useSelector((state: RootState) => state.oldDesignState.colorGradientXrf) as GradientStop[];
    const colorGradientMinerals = useSelector((state: RootState) => state.oldDesignState.colorGradientMinerals) as GradientStop[];
    const colorByElementXrf = useSelector((state: RootState) => state.oldDesignState.colorByElementXrf) as boolean;
    const colorByElementMinerals = useSelector((state: RootState) => state.oldDesignState.colorByElementMinerals) as boolean;
    const allElements = useMemo(() => {
        const merge = (el: any) => {
            const key = elementKey(el);
            const overridden = Object.prototype.hasOwnProperty.call(activeOverrides, key);
            return overridden ? {...el, active: !!activeOverrides[key]} : el;
        };
        const out: any[] = [];
        if (dataSources.includes('xrf')) out.push(...xrfElements.map(merge));
        if (dataSources.includes('minerals')) out.push(...mineralsElements.map(merge));
        return out;
    }, [dataSources, xrfElements, mineralsElements, activeOverrides]);
    const elements = useMemo(() => allElements.filter((el: any) => el.active), [allElements]);

    // Boreholes are passed through unchanged — the series builder reads the
    // right measurement array per element source, so no swap is needed.
    const selectedBoreholes = useMemo(() => selectedBoreholesRaw ?? [], [selectedBoreholesRaw]);
    const {fetchDetails} = useDrillholeDetails();

    const [selectedDrillholeIds, setSelectedDrillholeIds] = useState<string[]>(() => {
        try {
            const raw = localStorage.getItem('coreViewerSelectedDrillholes');
            if (raw) {
                const arr = JSON.parse(raw);
                if (Array.isArray(arr)) return arr;
            }
        } catch (_) {}
        return [];
    });
    const isInitialMount = useRef(true);
    const [display, setDisplay] = useState<number[]>([]);
    const [sharedView, setSharedView] = useState<TileViewSyncState | null>(() => {
        try {
            const raw = localStorage.getItem('coreViewerView');
            if (raw) return JSON.parse(raw);
        } catch (_) {}
        return null;
    });
    const [viewExtents, setViewExtents] = useState<[number, number] | null>(() => {
        try {
            const raw = localStorage.getItem('coreViewerViewExtents');
            if (raw) {
                const arr = JSON.parse(raw);
                if (Array.isArray(arr) && arr.length === 2) return [Number(arr[0]), Number(arr[1])];
            }
        } catch (_) {}
        return null;
    });
    const [sharedScrollLeft, setSharedScrollLeft] = useState<number>(() => {
        try {
            const raw = localStorage.getItem('coreViewerChartScrollLeft');
            if (raw != null) return Number(raw) || 0;
        } catch (_) {}
        return 0;
    });

    useEffect(() => {
        if (sharedView) {
            try { localStorage.setItem('coreViewerView', JSON.stringify(sharedView)); } catch (_) {}
        }
    }, [sharedView]);

    useEffect(() => {
        if (viewExtents) {
            try { localStorage.setItem('coreViewerViewExtents', JSON.stringify(viewExtents)); } catch (_) {}
        }
    }, [viewExtents]);

    useEffect(() => {
        try { localStorage.setItem('coreViewerChartScrollLeft', String(sharedScrollLeft)); } catch (_) {}
    }, [sharedScrollLeft]);
    const [activeLayer, setActiveLayer] = useState<string>(() => {
        try {
            return localStorage.getItem('coreViewerActiveLayer') ?? 'rgb';
        } catch (_) {
            return 'rgb';
        }
    });
    useEffect(() => {
        try {
            localStorage.setItem('coreViewerActiveLayer', activeLayer);
        } catch (_){ }
    }, [activeLayer]);
    const [tileSplitterSizes, setTileSplitterSizes] = useState<number[]>(() => {
        try {
            const raw = localStorage.getItem('tileSplitterSizes');
            if (raw) {
                const arr = JSON.parse(raw);
                if (Array.isArray(arr) && arr.length === 2) return arr;
            }
        } catch (_) {}
        return [30, 70];
    });
    const handleTileSplitterResize = useCallback((sizes: number[]) => {
        setTileSplitterSizes(sizes);
        localStorage.setItem('tileSplitterSizes', JSON.stringify(sizes));
    }, []);

    const viewSyncBusRef = useRef(new ViewSyncBus());
    const viewSyncBus = viewSyncBusRef.current;
    if (isInitialMount.current && sharedView) {
        viewSyncBus.current = sharedView;
    }

    const [xAxisMax, setXAxisMax] = useState<number>(() => {
        const val = localStorage.getItem('coreViewerXAxisMax');
        return val ? Number(val) : 1;
    });
    const [xAxisMin, setXAxisMin] = useState<number>(() => {
        const val = localStorage.getItem('coreViewerXAxisMin');
        return val ? Number(val) : 0;
    });
    const [elementMaxValues, setElementMaxValues] = useState<Record<string, number>>(() => {
        try {
            const raw = localStorage.getItem('coreViewerElementMaxValues');
            if (raw) return JSON.parse(raw);
        } catch (_) {}
        return {};
    });
    const [elementMinValues, setElementMinValues] = useState<Record<string, number>>(() => {
        try {
            const raw = localStorage.getItem('coreViewerElementMinValues');
            if (raw) return JSON.parse(raw);
        } catch (_) {}
        return {};
    });
    const handleElementMaxChange = useCallback((key: string, value: number | null) => {
        setElementMaxValues(prev => {
            const next = value === null ? {...prev} : {...prev, [key]: value};
            if (value === null) delete next[key];
            return next;
        });
    }, []);
    const handleElementMinChange = useCallback((key: string, value: number | null) => {
        setElementMinValues(prev => {
            const next = value === null ? {...prev} : {...prev, [key]: value};
            if (value === null) delete next[key];
            return next;
        });
    }, []);
    const [chartMaxValues, setChartMaxValues] = useState<Record<number, number>>(() => {
        try {
            const raw = localStorage.getItem('coreViewerChartMaxValues');
            if (raw) return JSON.parse(raw);
        } catch (_) {}
        return {};
    });
    const [chartMinValues, setChartMinValues] = useState<Record<number, number>>(() => {
        try {
            const raw = localStorage.getItem('coreViewerChartMinValues');
            if (raw) return JSON.parse(raw);
        } catch (_) {}
        return {};
    });
    const handleChartMaxChange = useCallback((chartIdx: number, value: number | null) => {
        setChartMaxValues(prev => {
            const next = value === null ? {...prev} : {...prev, [chartIdx]: value};
            if (value === null) delete next[chartIdx];
            return next;
        });
    }, []);
    const handleChartMinChange = useCallback((chartIdx: number, value: number | null) => {
        setChartMinValues(prev => {
            const next = value === null ? {...prev} : {...prev, [chartIdx]: value};
            if (value === null) delete next[chartIdx];
            return next;
        });
    }, []);
    const [chartFixedWidth, setChartFixedWidth] = useState<number | null>(() => {
        const val = localStorage.getItem('coreViewerChartFixedWidth');
        if (val === null || val === '') return null;
        const num = Number(val);
        return isFinite(num) && num > 0 ? num : null;
    });
    useEffect(() => {
        if (chartFixedWidth === null) {
            localStorage.removeItem('coreViewerChartFixedWidth');
        } else {
            localStorage.setItem('coreViewerChartFixedWidth', String(chartFixedWidth));
        }
    }, [chartFixedWidth]);
    const [titleSize, setTitleSize] = useState<number>(() => {
        const raw = localStorage.getItem('coreViewerTitleSize');
        if (raw == null) return 5;
        const parsed = parseInt(raw, 10);
        return Number.isFinite(parsed) && parsed >= 1 && parsed <= 10 ? parsed : 5;
    });
    useEffect(() => {
        localStorage.setItem('coreViewerTitleSize', String(titleSize));
    }, [titleSize]);
    const [layoutDirection, setLayoutDirection] = useState<string>(
        () => localStorage.getItem('coreViewerLayoutDirection') ?? 'vertical'
    );
    const [combinedChart, setCombinedChart] = useState<boolean>(
        () => localStorage.getItem('coreViewerCombinedChart') === 'true'
    );
    const [combinedChartCount, setCombinedChartCount] = useState<number>(() => {
        const val = localStorage.getItem('coreViewerCombinedChartCount');
        return val ? Number(val) : 1;
    });
    const [legendHideInactive, setLegendHideInactive] = useState<boolean>(() => {
        return localStorage.getItem('coreViewerLegendHideInactive') === 'true';
    });
    const [showMarkers, setShowMarkers] = useState<boolean>(() => {
        const val = localStorage.getItem('coreViewerShowMarkers');
        return val === null ? true : val === 'true';
    });
    const [elementChartIndex, setElementChartIndex] = useState<Record<string, number>>(() => {
        try {
            const raw = localStorage.getItem('coreViewerElementChartIndex');
            if (raw) return JSON.parse(raw);
        } catch (_) {}
        return {};
    });
    const handleElementChartIndexChange = useCallback((key: string, value: number) => {
        setElementChartIndex(prev => ({...prev, [key]: value}));
    }, []);

    // Template Logic
    interface CoreViewerTemplateData {
        selectedDrillholeIds: string[];
        activeLayer: string;
        layoutDirection: string;
        combinedChart: boolean;
        combinedChartCount: number;
        xAxisMax: number;
        xAxisMin: number;
        elementMaxValues: Record<string, number>;
        elementMinValues: Record<string, number>;
        chartMaxValues: Record<number, number>;
        chartMinValues: Record<number, number>;
        elementChartIndex: Record<string, number>;
        dataSources: DataSource[];
        hideInactive: boolean;
        showMarkers: boolean;
        tileSplitterSizes: number[];
        sharedView: TileViewSyncState | null;
        viewExtents: [number, number] | null;
        sharedScrollLeft: number;
        chartFixedWidth: number | null;
        activeOverrides?: Record<string, boolean>;
        colorGradientXrf?: GradientStop[];
        colorGradientMinerals?: GradientStop[];
        colorByElementXrf?: boolean;
        colorByElementMinerals?: boolean;
        titleSize?: number;
        // Legacy field for templates saved before multi-source
        dataSource?: DataSource;
    }

    const {templates, addTemplate, updateTemplate, deleteTemplate} = useTemplates<CoreViewerTemplateData>('coreViewerTemplates');
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(() => {
        return localStorage.getItem('coreViewerSelectedTemplateId');
    });
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [newTemplateName, setNewTemplateName] = useState('');

    const handleApplyTemplate = useCallback((id: string) => {
        const template = templates.find(t => t.id === id);
        if (template) {
            setSelectedTemplateId(id);
            setSelectedDrillholeIds(template.data.selectedDrillholeIds);
            setActiveLayer(template.data.activeLayer);
            setLayoutDirection(template.data.layoutDirection);
            setCombinedChart(template.data.combinedChart);
            setCombinedChartCount(template.data.combinedChartCount);
            setXAxisMax(template.data.xAxisMax);
            setXAxisMin(template.data.xAxisMin ?? 0);
            setElementMaxValues(template.data.elementMaxValues);
            setElementMinValues(template.data.elementMinValues ?? {});
            setChartMaxValues(template.data.chartMaxValues);
            setChartMinValues(template.data.chartMinValues ?? {});
            setElementChartIndex(template.data.elementChartIndex);
            setLegendHideInactive(!!template.data.hideInactive);
            setShowMarkers(template.data.showMarkers !== false);
            setChartFixedWidth(template.data.chartFixedWidth ?? null);
            if (template.data.sharedView) {
                const view = {...template.data.sharedView, sourceId: `template-${Date.now()}`};
                setSharedView(view);
                viewSyncBus.current = view;
            }
            if (template.data.viewExtents) setViewExtents(template.data.viewExtents);
            if (template.data.sharedScrollLeft != null) setSharedScrollLeft(template.data.sharedScrollLeft);
            if (Array.isArray(template.data.tileSplitterSizes) && template.data.tileSplitterSizes.length === 2) {
                setTileSplitterSizes(template.data.tileSplitterSizes);
                try {
                    localStorage.setItem('tileSplitterSizes', JSON.stringify(template.data.tileSplitterSizes));
                } catch (_) {}
            }
            const sources = template.data.dataSources
                ?? (template.data.dataSource ? [template.data.dataSource] : null);
            if (sources && sources.length > 0) {
                dispatch(setCoreViewerDataSources(sources));
            }
            dispatch(setCoreViewerActiveOverrides(template.data.activeOverrides ?? {}));
            if (template.data.colorGradientXrf) dispatch(setColorGradientXrf(template.data.colorGradientXrf));
            if (template.data.colorGradientMinerals) dispatch(setColorGradientMinerals(template.data.colorGradientMinerals));
            if (typeof template.data.colorByElementXrf === 'boolean') dispatch(setColorByElementXrf(template.data.colorByElementXrf));
            if (typeof template.data.colorByElementMinerals === 'boolean') dispatch(setColorByElementMinerals(template.data.colorByElementMinerals));
            if (typeof template.data.titleSize === 'number') setTitleSize(Math.max(1, Math.min(10, template.data.titleSize)));
        }
    }, [templates, dispatch, viewSyncBus]);

    const handleSaveTemplate = useCallback(async () => {
        const currentData: CoreViewerTemplateData = {
            selectedDrillholeIds,
            activeLayer,
            layoutDirection,
            combinedChart,
            combinedChartCount,
            xAxisMax,
            xAxisMin,
            elementMaxValues,
            elementMinValues,
            chartMaxValues,
            chartMinValues,
            elementChartIndex,
            dataSources,
            hideInactive: legendHideInactive,
            showMarkers,
            tileSplitterSizes,
            sharedView,
            viewExtents,
            sharedScrollLeft,
            chartFixedWidth,
            activeOverrides,
            colorGradientXrf,
            colorGradientMinerals,
            colorByElementXrf,
            colorByElementMinerals,
            titleSize,
        };

        const existing = templates.find(t => t.name.toLowerCase() === newTemplateName.toLowerCase());
        if (existing) {
            await updateTemplate(existing.id, currentData);
            setSelectedTemplateId(existing.id);
        } else {
            const created = await addTemplate(newTemplateName, currentData);
            if (created) setSelectedTemplateId(created.id);
        }
        setShowSaveDialog(false);
    }, [newTemplateName, templates, selectedDrillholeIds, activeLayer, layoutDirection, combinedChart, combinedChartCount, xAxisMax, xAxisMin, elementMaxValues, elementMinValues, chartMaxValues, chartMinValues, elementChartIndex, dataSources, legendHideInactive, showMarkers, tileSplitterSizes, sharedView, viewExtents, sharedScrollLeft, chartFixedWidth, activeOverrides, colorGradientXrf, colorGradientMinerals, colorByElementXrf, colorByElementMinerals, titleSize, updateTemplate, addTemplate]);

    const handleDeleteTemplate = useCallback(() => {
        if (selectedTemplateId) {
            deleteTemplate(selectedTemplateId);
            setSelectedTemplateId(null);
        }
    }, [selectedTemplateId, deleteTemplate]);

    // Deselection logic
    useEffect(() => {
        if (!selectedTemplateId) return;
        const template = templates.find(t => t.id === selectedTemplateId);
        if (!template) return;

        const currentData = {
            selectedDrillholeIds,
            activeLayer,
            layoutDirection,
            combinedChart,
            combinedChartCount,
            xAxisMax,
            xAxisMin,
            elementMaxValues,
            elementMinValues,
            chartMaxValues,
            chartMinValues,
            elementChartIndex,
            dataSources,
            hideInactive: legendHideInactive,
            showMarkers,
            tileSplitterSizes,
            sharedView,
            viewExtents,
            sharedScrollLeft,
            chartFixedWidth,
            activeOverrides,
            colorGradientXrf,
            colorGradientMinerals,
            colorByElementXrf,
            colorByElementMinerals,
            titleSize,
        };

        const templateSources = template.data.dataSources
            ?? (template.data.dataSource ? [template.data.dataSource] : dataSources);
        const comparableTemplate = {
            ...template.data,
            dataSources: templateSources,
            tileSplitterSizes: template.data.tileSplitterSizes ?? tileSplitterSizes,
            sharedView: template.data.sharedView ?? sharedView,
            viewExtents: template.data.viewExtents ?? viewExtents,
            sharedScrollLeft: template.data.sharedScrollLeft ?? sharedScrollLeft,
            chartFixedWidth: template.data.chartFixedWidth ?? null,
            activeOverrides: template.data.activeOverrides ?? {},
            colorGradientXrf: template.data.colorGradientXrf ?? colorGradientXrf,
            colorGradientMinerals: template.data.colorGradientMinerals ?? colorGradientMinerals,
            colorByElementXrf: template.data.colorByElementXrf ?? colorByElementXrf,
            colorByElementMinerals: template.data.colorByElementMinerals ?? colorByElementMinerals,
            titleSize: template.data.titleSize ?? titleSize,
        };
        delete (comparableTemplate as any).dataSource;

        if (JSON.stringify(currentData) !== JSON.stringify(comparableTemplate)) {
            setSelectedTemplateId(null);
        }
    }, [selectedDrillholeIds, activeLayer, layoutDirection, combinedChart, combinedChartCount, xAxisMax, xAxisMin, elementMaxValues, elementMinValues, chartMaxValues, chartMinValues, elementChartIndex, dataSources, legendHideInactive, showMarkers, tileSplitterSizes, sharedView, viewExtents, sharedScrollLeft, chartFixedWidth, activeOverrides, colorGradientXrf, colorGradientMinerals, colorByElementXrf, colorByElementMinerals, titleSize, selectedTemplateId, templates]);

    // Load mineral list from first selected drillhole's tile metadata
    const firstId = selectedDrillholeIds[0] ?? '';
    const [tileMetadata, setTileMetadata] = useState<TileMetadata | null>(null);
    useEffect(() => {
        if (!firstId || !projectId) {
            setTileMetadata(null);
            return;
        }
        fetch(`${settings.drillholeApiUrl}/projects/${projectId}/drillholes/${firstId}/tiles/metadata`)
            .then(r => r.ok ? r.json() : null)
            .then(setTileMetadata)
            .catch(() => setTileMetadata(null));
    }, [firstId, projectId]);
    const mineralOptions = useMemo(() => {
        if (!tileMetadata?.minerals) return [];
        return Object.keys(tileMetadata.minerals);
    }, [tileMetadata]);

    const maxTileVpWidth = useMemo(() => {
        if (!tileMetadata) return null;
        const layer: TileLayerMeta | undefined =
            activeLayer === 'rgb'
                ? tileMetadata.rgb
                : (tileMetadata.minerals[activeLayer] ?? tileMetadata.rgb);
        if (!layer) return null;
        let max = 0;
        for (const img of layer.images) {
            if (img.height > 0) {
                const vpWidth = (img.depth_to - img.depth_from) * img.width / img.height;
                if (vpWidth > max) max = vpWidth;
            }
        }
        return max > 0 ? max : null;
    }, [tileMetadata, activeLayer]);

    // Reload drillhole data when tiles have been generated
    const handleTilesGenerated = useCallback(() => {
        // Re-fetch tile metadata for mineral options
        if (firstId && projectId) {
            fetch(`${settings.drillholeApiUrl}/projects/${projectId}/drillholes/${firstId}/tiles/metadata`)
                .then(r => r.ok ? r.json() : null)
                .then(setTileMetadata)
                .catch(() => {});
        }
        // Re-fetch drillhole details
        const allToLoad = selectedDrillholeIds
            .map(id => data.find((d: any) => d.hole_id === id))
            .filter(Boolean);
        if (allToLoad.length > 0) {
            fetchDetails(allToLoad);
        }
    }, [firstId, projectId, selectedDrillholeIds, data, fetchDetails]);

    const contentDivRef = useRef<HTMLDivElement>(null);
    const autoAdjustTimerRef = useRef(0);
    const autoAdjustDisabledRef = useRef(false);

    // Throttled view change — batch rapid updates into one state change per rAF
    const pendingViewRef = useRef<TileViewSyncState | null>(null);
    const viewRafRef = useRef(0);
    const lastExtentsRef = useRef<[number, number]>([0, 0]);
    // When set, scroll-based view changes are ignored until this timestamp
    const manualLockUntilRef = useRef(0);
    const handleViewChange = useCallback((view: TileViewSyncState) => {
        // Ignore scroll-based updates shortly after a manual input to prevent overwriting
        if (performance.now() < manualLockUntilRef.current) return;
        pendingViewRef.current = view;
        if (viewRafRef.current) return;
        viewRafRef.current = requestAnimationFrame(() => {
            viewRafRef.current = 0;
            if (pendingViewRef.current) {
                setSharedView(pendingViewRef.current);
                pendingViewRef.current = null;
            }
        });
    }, []);

    const handleExtentsChangeGlobal = useCallback((min: number, max: number) => {
        lastExtentsRef.current = [min, max];
        // Only update viewExtents if we're not currently doing a manual update to prevent feedback loops
        setViewExtents([min, max]);
    }, []);

    const handleScrollChange = useCallback((scrollLeft: number) => {
        setSharedScrollLeft(scrollLeft);
    }, []);

    // Vorauswahl aus Redux (Karte/Liste) übernehmen - nur beim ersten Mount, falls localStorage leer ist
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            if (selectedDrillholeIds.length === 0 && selectedBoreholes && selectedBoreholes.length > 0) {
                const ids = selectedBoreholes.map((b: any) => b.hole_id).filter(Boolean);
                if (ids.length > 0) {
                    setSelectedDrillholeIds(ids);
                }
            }
        }
    }, [selectedBoreholes]);

    // Sync selectedDrillholeIds with the GeoMap line whenever the line changes.
    // The localStorage tracker ensures manual dropdown changes are preserved until
    // the line itself is changed on the map.
    useEffect(() => {
        if (!selectLine || !withCoords || withCoords.length === 0 || mapBufferOffset <= 0) {
            if (!selectLine) localStorage.removeItem('coreViewerLastSyncedSelectLine');
            return;
        }
        const currentLineKey = JSON.stringify(selectLine);
        const lastSyncedKey = localStorage.getItem('coreViewerLastSyncedSelectLine');
        if (currentLineKey === lastSyncedKey) return;

        const c1 = [selectLine.x1, selectLine.y1];
        const c2 = [selectLine.x2, selectLine.y2];
        const ls = turf.lineString([c1, c2]);
        const buff = turf.buffer(ls, mapBufferOffset / 1.6, {units: 'meters'});
        const ids: string[] = [];
        const capturedHoles: any[] = [];
        for (const item of withCoords) {
            if (!item.collar || item.collar.LONGITUDE == null || item.collar.LATITUDE == null) continue;
            const pt = turf.point([item.collar.LONGITUDE, item.collar.LATITUDE]);
            if (turf.booleanPointInPolygon(pt, buff)) {
                ids.push(item.hole_id);
                capturedHoles.push(item);
            }
        }
        setSelectedDrillholeIds(ids);
        if (capturedHoles.length > 0) {
            fetchDetails(capturedHoles, [selectLine.x1, selectLine.y1]);
        }
        localStorage.setItem('coreViewerLastSyncedSelectLine', currentLineKey);
    }, [selectLine, withCoords, mapBufferOffset, fetchDetails]);

    // Auswahl in localStorage persistieren
    useEffect(() => {
        localStorage.setItem('coreViewerSelectedDrillholes', JSON.stringify(selectedDrillholeIds));
    }, [selectedDrillholeIds]);

    // Details nachladen für Bohrlöcher, die noch nicht in Redux sind
    useEffect(() => {
        if (selectedDrillholeIds.length === 0 || data.length === 0) return;
        const enrichedIds = new Set((selectedBoreholes || []).map((b: any) => b.hole_id));
        const missing = selectedDrillholeIds.filter(id => !enrichedIds.has(id));
        if (missing.length === 0) return;
        const missingDrillholes = missing
            .map(id => data.find((d: any) => d.hole_id === id))
            .filter(Boolean);
        if (missingDrillholes.length === 0) return;
        const allToLoad = selectedDrillholeIds
            .map(id => data.find((d: any) => d.hole_id === id))
            .filter(Boolean);
        fetchDetails(allToLoad);
    }, [selectedDrillholeIds, data]);

    // Angereicherte Daten aus Redux bevorzugen
    const selectedDrillholes = useMemo(() => {
        return selectedDrillholeIds
            .map(id => {
                const enriched = (selectedBoreholes || []).find((b: any) => b.hole_id === id);
                return enriched || null;
            })
            .filter(Boolean)
            .sort((a: any, b: any) => a.hole_id.localeCompare(b.hole_id));
    }, [selectedDrillholeIds, selectedBoreholes]);

    // Scale: real-world meters per screen meter
    const scaleLabel = useMemo(() => {
        if (!sharedView?.metersPerPixel) return undefined;

        // Detect browser zoom level (1.0 = 100%, 1.5 = 150%, etc.)
        const browserZoom = window.devicePixelRatio / (window.screen?.availWidth / document.documentElement.clientWidth || 1);

        // Try to estimate actual screen DPI by measuring a temporary element
        // Fallback to standard 96 DPI if measurement fails
        let screenDPI = 96;
        try {
            const testDiv = document.createElement('div');
            testDiv.style.width = '1in';
            testDiv.style.visibility = 'hidden';
            testDiv.style.position = 'absolute';
            document.body.appendChild(testDiv);
            screenDPI = testDiv.offsetWidth;
            document.body.removeChild(testDiv);
        } catch (e) {
            // Fallback to standard DPI
        }

        // Calculate CSS pixels per physical screen meter
        // Accounting for: device pixel ratio, browser zoom, and actual screen DPI
        const cssPixelsPerInch = screenDPI / browserZoom;
        const cssPixelsPerScreenMeter = cssPixelsPerInch / 0.0254;

        const ratio = sharedView.metersPerPixel * cssPixelsPerScreenMeter;

        return `1:${Math.max(1, Math.round(ratio))}`;
    }, [sharedView]);

    // Depths per selected drillhole from measurement data
    const depthsInfo = useMemo(() => {
        return selectedDrillholes.map((dh: any) => {
            if (viewExtents) {
                return {
                    label: dh.hole_id,
                    from: viewExtents[0],
                    to: viewExtents[1],
                };
            }

            let minDepth = Infinity;
            let maxDepth = -Infinity;
            const scan = (measurements: any[] | undefined) => {
                if (!measurements) return;
                for (const mea of measurements) {
                    for (const xrf of mea.mea_xrfres ?? []) {
                        for (const spe of xrf.xrfres_spe ?? []) {
                            const d = Math.abs(spe.position);
                            if (d < minDepth) minDepth = d;
                            if (d > maxDepth) maxDepth = d;
                        }
                    }
                }
            };
            scan(dh.dri_mea);
            scan(dh.dri_mea_minerals);
            return {
                label: dh.hole_id,
                from: minDepth === Infinity ? 0 : minDepth,
                to: maxDepth === -Infinity ? 0 : maxDepth,
            };
        });
    }, [selectedDrillholes, viewExtents]);

    const getCssPixelsPerScreenMeter = useCallback(() => {
        const browserZoom = window.devicePixelRatio / (window.screen?.availWidth / document.documentElement.clientWidth || 1);
        let screenDPI = 96;
        try {
            const testDiv = document.createElement('div');
            testDiv.style.width = '1in';
            testDiv.style.visibility = 'hidden';
            testDiv.style.position = 'absolute';
            document.body.appendChild(testDiv);
            screenDPI = testDiv.offsetWidth;
            document.body.removeChild(testDiv);
        } catch (e) { }
        const cssPixelsPerInch = screenDPI / browserZoom;
        return cssPixelsPerInch / 0.0254;
    }, []);

    const handleScaleChange = useCallback((scaleStr: string) => {
        let ratio: number;
        if (scaleStr.includes(':')) {
            const parts = scaleStr.split(':').map(s => s.trim());
            const part1 = Number(parts[0]);
            const part2 = Number(parts[1]);

            if (!isNaN(part1) && !isNaN(part2) && part1 > 0 && part2 > 0) {
                ratio = part1 / part2;
            } else return;
        } else {
            const val = Number(scaleStr);
            if (isNaN(val) || val <= 0) return;
            ratio = 1 / val;
        }

        const cssPixelsPerScreenMeter = getCssPixelsPerScreenMeter();
        const metersPerPixel = ratio / cssPixelsPerScreenMeter;

        // Fallback center: before the TileViewer has emitted a view we would
        // otherwise jump to depth 0 (which zoomed the viewer to the hole top).
        const extents = viewExtents ?? (
            (lastExtentsRef.current[0] !== 0 || lastExtentsRef.current[1] !== 0)
                ? lastExtentsRef.current
                : null
        );
        const fallbackCenter = extents ? (extents[0] + extents[1]) / 2 : 0;

        // Lock out scroll-based view updates for 1 second after manual input
        manualLockUntilRef.current = performance.now() + 1000;
        setSharedView({
            centerDepth: sharedView?.centerDepth ?? fallbackCenter,
            centerX: sharedView?.centerX ?? 0.5,
            metersPerPixel,
            sourceId: `manual-${Date.now()}`
        });
    }, [getCssPixelsPerScreenMeter, sharedView, viewExtents]);

    const handleDepthChange = useCallback((from: number, to: number) => {
        const centerDepth = (from + to) / 2;
        const rangeMeters = Math.abs(to - from);

        // Estimate metersPerPixel based on container height if possible
        let metersPerPixel = sharedView?.metersPerPixel ?? 0.001;
        if (rangeMeters > 0) {
            // Try to find the actual viewer height from the DOM
            let viewerHeight = 800;
            const viewerEl = document.querySelector('.tile-viewer-container');
            if (viewerEl) {
                viewerHeight = viewerEl.clientHeight;
            }

            metersPerPixel = rangeMeters / (viewerHeight || 800);
        }

        // Lock out scroll-based view updates for 1 second after manual input
        manualLockUntilRef.current = performance.now() + 1000;
        setSharedView({
            centerDepth,
            centerX: sharedView?.centerX ?? 0.5,
            metersPerPixel,
            sourceId: `manual-${Date.now()}`
        });
    }, [sharedView]);

    // Global max value across all selected boreholes for x-axis scaling.
    // Only elements that correspond to currently selected data sources contribute,
    // so switching sources doesn't leak values from an unused set.
    const globalMaxInfo = useMemo(() => {
        let max = 0;
        let maxElementId: number | null = null;
        let maxDepth: number | null = null;
        let maxSource: string | null = null;
        const scan = (measurements: any[] | undefined, source: 'xrf' | 'minerals') => {
            if (!measurements) return;
            // Only consider elements that belong to this source
            const sourceElementIds = new Set(
                elements.filter((el: any) => (el.source ?? 'xrf') === source).map((el: any) => el.id)
            );
            if (sourceElementIds.size === 0) return;
            for (const mea of measurements) {
                for (const xrf of mea.mea_xrfres ?? []) {
                    for (const spe of xrf.xrfres_spe ?? []) {
                        for (const speres of spe.spe_speres ?? []) {
                            if (sourceElementIds.has(speres.element_id) && speres.value > max) {
                                max = speres.value;
                                maxElementId = speres.element_id;
                                maxDepth = spe.position * -1;
                                maxSource = source;
                            }
                        }
                    }
                }
            }
        };
        for (const dh of selectedDrillholes) {
            if (dataSources.includes('xrf')) scan(dh.dri_mea, 'xrf');
            if (dataSources.includes('minerals')) scan(dh.dri_mea_minerals, 'minerals');
        }
        const maxElement = maxElementId != null ? elements.find((el: any) => el.id === maxElementId) : null;
        return {
            value: max || 1,
            elementName: maxElement?.name ?? null,
            elementUnit: maxElement?.unit ?? null,
            elementColor: maxElement?.color ?? null,
            depth: maxDepth,
        };
    }, [selectedDrillholes, dataSources, elements]);
    const globalMaxValue = globalMaxInfo.value;

    // Per combined-chart max value: across all selected drillholes, considering
    // only the elements assigned to each chart. Used as the default x-axis max
    // for each individual chart in combined multi-chart mode.
    const chartGroupMaxValues = useMemo(() => {
        const count = Math.max(1, combinedChartCount);
        const maxes = new Array(count).fill(0);
        const chartSourceIds: { xrf: Set<number>; minerals: Set<number> }[] =
            Array.from({length: count}, () => ({xrf: new Set<number>(), minerals: new Set<number>()}));
        for (const el of elements) {
            const cIdx = Math.min((elementChartIndex[elementKey(el)] ?? 1) - 1, count - 1);
            const src: 'xrf' | 'minerals' = (el.source ?? 'xrf') === 'minerals' ? 'minerals' : 'xrf';
            chartSourceIds[cIdx][src].add(el.id);
        }
        const scan = (measurements: any[] | undefined, source: 'xrf' | 'minerals') => {
            if (!measurements) return;
            for (const mea of measurements) {
                for (const xrf of mea.mea_xrfres ?? []) {
                    for (const spe of xrf.xrfres_spe ?? []) {
                        for (const speres of spe.spe_speres ?? []) {
                            for (let i = 0; i < count; i++) {
                                if (chartSourceIds[i][source].has(speres.element_id) && speres.value > maxes[i]) {
                                    maxes[i] = speres.value;
                                }
                            }
                        }
                    }
                }
            }
        };
        for (const dh of selectedDrillholes) {
            if (dataSources.includes('xrf')) scan(dh.dri_mea, 'xrf');
            if (dataSources.includes('minerals')) scan(dh.dri_mea_minerals, 'minerals');
        }
        return maxes;
    }, [selectedDrillholes, dataSources, elements, elementChartIndex, combinedChartCount]);

    const globalMinInfo = useMemo(() => {
        let min: number | null = null;
        let minElementId: number | null = null;
        let minDepth: number | null = null;
        let minSource: string | null = null;
        const scan = (measurements: any[] | undefined, source: 'xrf' | 'minerals') => {
            if (!measurements) return;
            const sourceElementIds = new Set(
                elements.filter((el: any) => (el.source ?? 'xrf') === source).map((el: any) => el.id)
            );
            if (sourceElementIds.size === 0) return;
            for (const mea of measurements) {
                for (const xrf of mea.mea_xrfres ?? []) {
                    for (const spe of xrf.xrfres_spe ?? []) {
                        for (const speres of spe.spe_speres ?? []) {
                            if (sourceElementIds.has(speres.element_id)) {
                                if (min === null || speres.value < min) {
                                    min = speres.value;
                                    minElementId = speres.element_id;
                                    minDepth = spe.position * -1;
                                    minSource = source;
                                }
                            }
                        }
                    }
                }
            }
        };
        for (const dh of selectedDrillholes) {
            if (dataSources.includes('xrf')) scan(dh.dri_mea, 'xrf');
            if (dataSources.includes('minerals')) scan(dh.dri_mea_minerals, 'minerals');
        }
        const minElement = minElementId != null ? elements.find((el: any) => el.id === minElementId) : null;
        return {
            value: min ?? 0,
            elementName: minElement?.name ?? null,
            elementUnit: minElement?.unit ?? null,
            elementColor: minElement?.color ?? null,
            depth: minDepth,
        };
    }, [selectedDrillholes, dataSources, elements]);
    const globalMinValue = globalMinInfo.value;


    useEffect(() => {
        localStorage.setItem('coreViewerLayoutDirection', layoutDirection);
    }, [layoutDirection]);
    useEffect(() => {
        localStorage.setItem('coreViewerCombinedChart', String(combinedChart));
    }, [combinedChart]);
    useEffect(() => {
        localStorage.setItem('coreViewerCombinedChartCount', String(combinedChartCount));
    }, [combinedChartCount]);
    useEffect(() => {
        localStorage.setItem('coreViewerLegendHideInactive', String(legendHideInactive));
    }, [legendHideInactive]);
    useEffect(() => {
        localStorage.setItem('coreViewerShowMarkers', String(showMarkers));
    }, [showMarkers]);
    useEffect(() => {
        localStorage.setItem('coreViewerXAxisMax', String(xAxisMax));
    }, [xAxisMax]);
    useEffect(() => {
        localStorage.setItem('coreViewerXAxisMin', String(xAxisMin));
    }, [xAxisMin]);
    useEffect(() => {
        localStorage.setItem('coreViewerElementMaxValues', JSON.stringify(elementMaxValues));
    }, [elementMaxValues]);
    useEffect(() => {
        localStorage.setItem('coreViewerElementMinValues', JSON.stringify(elementMinValues));
    }, [elementMinValues]);
    useEffect(() => {
        localStorage.setItem('coreViewerChartMaxValues', JSON.stringify(chartMaxValues));
    }, [chartMaxValues]);
    useEffect(() => {
        localStorage.setItem('coreViewerChartMinValues', JSON.stringify(chartMinValues));
    }, [chartMinValues]);
    useEffect(() => {
        localStorage.setItem('coreViewerElementChartIndex', JSON.stringify(elementChartIndex));
    }, [elementChartIndex]);
    useEffect(() => {
        if (selectedTemplateId) {
            localStorage.setItem('coreViewerSelectedTemplateId', selectedTemplateId);
        } else {
            localStorage.removeItem('coreViewerSelectedTemplateId');
        }
    }, [selectedTemplateId]);

    const [isDark, setIsDark] = useState<boolean>(
        () => (localStorage.getItem('Theme') ?? 'dark') === 'dark'
    );
    useEffect(() => {
        const handler = (e: Event) => setIsDark((e as CustomEvent).detail === 'dark');
        window.addEventListener('themechange', handler);
        return () => window.removeEventListener('themechange', handler);
    }, []);

    useEffect(() => {
        setXAxisMax(globalMaxValue);
    }, [globalMaxValue]);
    useEffect(() => {
        setXAxisMin(globalMinValue);
    }, [globalMinValue]);

    // Auto-adjust TileViewer pane width to fit the widest tile on zoom.
    // Only fires when the zoom originated from a TileViewer user interaction
    // (sourceId = drillhole name, not 'manual-...' / 'template-...').
    // Disabled permanently once the user has manually dragged the splitter.
    const maxTileVpWidthRef = useRef(maxTileVpWidth);
    maxTileVpWidthRef.current = maxTileVpWidth;
    const selectedDrillholesCountRef = useRef(selectedDrillholes.length);
    selectedDrillholesCountRef.current = selectedDrillholes.length;
    const layoutDirectionRef = useRef(layoutDirection);
    layoutDirectionRef.current = layoutDirection;
    const handleManualSplitterAdjust = useCallback(() => {
        autoAdjustDisabledRef.current = true;
        if (autoAdjustTimerRef.current) {
            clearTimeout(autoAdjustTimerRef.current);
            autoAdjustTimerRef.current = 0;
        }
    }, []);
    useEffect(() => {
        if (!sharedView?.metersPerPixel || !sharedView.sourceId) return;
        // Skip programmatic zoom changes
        if (sharedView.sourceId.startsWith('manual-') || sharedView.sourceId.startsWith('template-')) return;
        // Skip if user has taken manual control of the splitter
        if (autoAdjustDisabledRef.current) return;
        if (autoAdjustTimerRef.current) clearTimeout(autoAdjustTimerRef.current);
        autoAdjustTimerRef.current = window.setTimeout(() => {
            if (autoAdjustDisabledRef.current) return;
            const vpWidth = maxTileVpWidthRef.current;
            if (!vpWidth) return;
            const container = contentDivRef.current;
            if (!container) return;
            const totalWidth = container.getBoundingClientRect().width;
            if (totalWidth <= 0) return;
            const perRowWidth = layoutDirectionRef.current === 'horizontal'
                ? totalWidth / Math.max(1, selectedDrillholesCountRef.current)
                : totalWidth;
            const tileWidthPx = vpWidth / sharedView.metersPerPixel;
            const neededPercent = Math.max(10, Math.min(80, (tileWidthPx / perRowWidth) * 100));
            setTileSplitterSizes([neededPercent, 100 - neededPercent]);
            localStorage.setItem('tileSplitterSizes', JSON.stringify([neededPercent, 100 - neededPercent]));
        }, 400);
        return () => clearTimeout(autoAdjustTimerRef.current);
    }, [sharedView?.metersPerPixel, sharedView?.sourceId]);

    // PDF export
    const viewerHandlesRef = useRef<Map<string, TileViewerHandle>>(new Map());
    const visibleDepthRef = useRef<[number, number] | null>(null);
    const [isExportingPdf, setIsExportingPdf] = useState(false);

    const handleViewerReady = useCallback((holeId: string, handle: TileViewerHandle) => {
        viewerHandlesRef.current.set(holeId, handle);
    }, []);

    const handleExtentsForPdf = useCallback((min: number, max: number) => {
        visibleDepthRef.current = [min, max];
    }, []);

    const handleExportPdf = useCallback(async () => {
        setIsExportingPdf(true);
        try {
            // Small delay to let the spinner render
            await new Promise(r => setTimeout(r, 50));

            const activeElements = elements.map((el: any) => ({
                id: el.id,
                name: el.name,
                unit: el.unit ?? '',
                color: el.color,
                shell: el.shell ?? '',
                sub_shell: el.sub_shell ?? '',
                source: el.source ?? 'xrf',
            }));

            const pdfDrillholes = selectedDrillholes.map((dh: any) => {
                const handle = viewerHandlesRef.current.get(dh.hole_id);
                return {
                    holeId: dh.hole_id,
                    tileImageDataUrl: handle?.getCanvasDataUrl() ?? null,
                    activeLayer,
                    seriesData: buildSeriesData(dh, activeElements),
                };
            });

            await exportCoreViewerPdf({
                drillholes: pdfDrillholes,
                elements: activeElements,
                xAxisMax,
                xAxisMin,
                elementMaxValues,
                elementMinValues,
                depthRange: visibleDepthRef.current,
                combinedChart,
                combinedChartCount,
                elementChartIndex,
                chartMaxValues,
                chartMinValues,
                chartGroupMaxValues,
            });
        } finally {
            setIsExportingPdf(false);
        }
    }, [selectedDrillholes, elements, activeLayer, xAxisMax, xAxisMin, elementMaxValues, elementMinValues, combinedChart, combinedChartCount, elementChartIndex, chartMaxValues, chartMinValues, chartGroupMaxValues]);

    const isControlBarCollapsed = useSelector((state: RootState) => state.oldDesignState.isControlBarCollapsed);

    // Cleanup rAF on unmount
    useEffect(() => {
        return () => {
            if (viewRafRef.current) cancelAnimationFrame(viewRafRef.current);
        };
    }, []);

    return (
        <div className='card w-full h-full mb-0'>
            <div style={{display: 'flex', height: '100%'}}>
                <div style={{flexShrink: 0, width: isControlBarCollapsed ? '36px' : '320px', overflow: isControlBarCollapsed ? 'hidden' : 'auto', transition: 'width 0.2s'}}>
                    <ControlBar selectedDrillhole={selectedDrillholes[0] ?? null} display={display}
                                setDisplay={setDisplay} showLegend={true} legendMultiSelect={true}
                                legendShowAmountColortable={false} scale={scaleLabel} depths={depthsInfo}
                                dataSources={dataSources}
                                onDataSourcesChange={(next) => dispatch(setCoreViewerDataSources(next))}
                                activeOverrides={activeOverrides}
                                onActiveOverridesChange={(next) => dispatch(setCoreViewerActiveOverrides(next))}
                                onScaleChange={handleScaleChange}
                                onDepthChange={handleDepthChange}
                                drillholeOptions={data.map((d: any) => ({value: d.hole_id, label: d.hole_id}))}
                                selectedDrillholeIds={selectedDrillholeIds}
                                onDrillholeIdsChange={setSelectedDrillholeIds}
                                elementMaxValues={elementMaxValues}
                                onElementMaxChange={handleElementMaxChange}
                                elementMinValues={elementMinValues}
                                onElementMinChange={handleElementMinChange}
                                combinedChart={combinedChart}
                                combinedChartCount={combinedChartCount}
                                legendHideInactive={legendHideInactive}
                                onLegendHideInactiveChange={setLegendHideInactive}
                                elementChartIndex={elementChartIndex}
                                onElementChartIndexChange={handleElementChartIndexChange}
                                templates={templates}
                                selectedTemplateId={selectedTemplateId}
                                onApplyTemplate={handleApplyTemplate}
                                onSaveTemplate={() => {
                                    const current = templates.find(t => t.id === selectedTemplateId);
                                    setNewTemplateName(current ? current.name : '');
                                    setShowSaveDialog(true);
                                }}
                                onDeleteTemplate={handleDeleteTemplate}
                                onExportPdf={handleExportPdf}
                                isExportingPdf={isExportingPdf}
                                extraControls={<>
                                    <h4>CoreViewer</h4>
                                    <Input edit={true} name={'imageLayer'}
                                           type={'dropdown'}
                                           label={'Image Layer'}
                                           dropdownOptions={[
                                               {value: 'rgb', label: 'RGB'},
                                               ...mineralOptions.map(m => ({value: m, label: m}))
                                           ]}
                                           value={activeLayer}
                                           onChange={(e: any) => {
                                               setActiveLayer(e.value ?? 'rgb');
                                           }}/>
                                    {activeLayer === 'Mineral_Map' && (
                                        <div className="field" style={{marginTop: '-0.5rem'}}>
                                            <div style={{
                                                display: 'grid',
                                                gridAutoFlow: 'column',
                                                gridTemplateColumns: '1fr 1fr',
                                                gridTemplateRows: `repeat(${Math.ceil(MINERAL_MAP_LEGEND.length / 2)}, auto)`,
                                                columnGap: '12px',
                                                rowGap: '2px',
                                                padding: '4px 0',
                                            }}>
                                                {MINERAL_MAP_LEGEND.map(({mineral, rgb}) => (
                                                    <div key={mineral} style={{display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', minWidth: 0}}>
                                                        <span style={{
                                                            display: 'inline-block',
                                                            width: '14px',
                                                            height: '14px',
                                                            borderRadius: '2px',
                                                            backgroundColor: `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`,
                                                            border: '1px solid var(--surface-border)',
                                                            flexShrink: 0,
                                                        }}/>
                                                        <span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{mineral}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <Input edit={true} name={'layoutDirection'}
                                           type={'dropdown'}
                                           label={'Layout'}
                                           dropdownOptions={[
                                               {value: 'vertical', label: 'Vertical'},
                                               {value: 'horizontal', label: 'Horizontal'},
                                           ]}
                                           value={layoutDirection}
                                           onChange={(e: any) => {
                                               setLayoutDirection(e.value ?? 'vertical');
                                           }}/>
                                    <Input edit={true} name={'showMarkers'}
                                           type={'checkbox'}
                                           label={'Show Markers'}
                                           value={showMarkers}
                                           onChange={(e: any) => {
                                               setShowMarkers(e.checked ?? e);
                                           }}/>
                                    <div style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap'}}>
                                        <Input edit={true} name={'combinedChart'}
                                               type={'checkbox'}
                                               label={'Combined'}
                                               value={combinedChart}
                                               onChange={(e: any) => {
                                                   setCombinedChart(e.checked ?? e);
                                               }}/>
                                        {combinedChart && (
                                            <input
                                                type="number"
                                                min={1}
                                                max={5}
                                                value={combinedChartCount}
                                                onChange={(e) => {
                                                    const val = parseInt(e.target.value, 10);
                                                    if (!isNaN(val) && val >= 1 && val <= 5) setCombinedChartCount(val);
                                                }}
                                                className="p-inputtext p-component"
                                                style={{width: '60px'}}
                                            />
                                        )}
                                    </div>
                                    <div className="field">
                                        <div className="flex">
                                            <h5 className="input-label">Chart Width</h5>
                                        </div>
                                        <div className="p-inputgroup">
                                            <InputNumber
                                                value={chartFixedWidth}
                                                placeholder="Auto"
                                                suffix=" px"
                                                onValueChange={(e) => setChartFixedWidth(e.value ?? null)}
                                            />
                                            <Button
                                                icon="pi pi-refresh"
                                                tooltip="Reset to auto (fit without scrolling)"
                                                tooltipOptions={{position: 'top'}}
                                                onClick={() => setChartFixedWidth(null)}
                                                className="p-button-secondary"
                                            />
                                        </div>
                                    </div>
                                    <div className="field">
                                        <div className="flex" style={{alignItems: 'center', justifyContent: 'space-between'}}>
                                            <h5 className="input-label">Title Size</h5>
                                            <span style={{fontSize: '11px', color: 'var(--text-color-secondary)'}}>{titleSize}/10</span>
                                        </div>
                                        <Slider
                                            value={titleSize}
                                            min={1}
                                            max={10}
                                            step={1}
                                            onChange={(e) => setTitleSize(Array.isArray(e.value) ? e.value[0] : e.value)}
                                        />
                                    </div>
                                    <div className="field">
                                        <div className="flex">
                                            <h5 className="input-label">Axis Min</h5>
                                        </div>
                                        {combinedChart && combinedChartCount > 1 ? (
                                            Array.from({length: combinedChartCount}, (_, i) => (
                                                <div key={i} className="p-inputgroup" style={{marginBottom: '4px'}}>
                                                    <span className="p-inputgroup-addon" style={{fontSize: '11px', minWidth: '56px'}}>Chart {i + 1}</span>
                                                    <InputNumber
                                                        value={chartMinValues[i] ?? xAxisMin}
                                                        maxFractionDigits={2}
                                                        onChange={(e) => handleChartMinChange(i, e.value ?? null)}
                                                    />
                                                    <Button
                                                        icon="pi pi-refresh"
                                                        tooltip={`Reset to global min (${globalMinValue})`}
                                                        tooltipOptions={{position: 'top'}}
                                                        onClick={() => handleChartMinChange(i, null)}
                                                        className="p-button-secondary"
                                                    />
                                                </div>
                                            ))
                                        ) : (
                                            <div className="p-inputgroup">
                                                <InputNumber
                                                    value={xAxisMin}
                                                    maxFractionDigits={2}
                                                    onChange={(e) => setXAxisMin(e.value ?? globalMinValue)}
                                                />
                                                <Button
                                                    icon="pi pi-refresh"
                                                    tooltip={`Reset to min (${globalMinValue})`}
                                                    tooltipOptions={{position: 'top'}}
                                                    onClick={() => setXAxisMin(globalMinValue)}
                                                    className="p-button-secondary"
                                                />
                                            </div>
                                        )}
                                        {/*{globalMinInfo.elementName && (*/}
                                        {/*    <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', fontSize: '12px'}}>*/}
                                        {/*        <span style={{*/}
                                        {/*            display: 'inline-block',*/}
                                        {/*            width: '12px',*/}
                                        {/*            height: '12px',*/}
                                        {/*            borderRadius: '2px',*/}
                                        {/*            backgroundColor: globalMinInfo.elementColor ?? '#888',*/}
                                        {/*            flexShrink: 0,*/}
                                        {/*        }}/>*/}
                                        {/*        <span>{globalMinInfo.elementName}: {Math.round(globalMinInfo.value * 100) / 100} @ {Math.round(globalMinInfo.depth ?? 0)}m</span>*/}
                                        {/*    </div>*/}
                                        {/*)}*/}
                                    </div>
                                    <div className="field">
                                        <div className="flex">
                                            <h5 className="input-label">Axis Max</h5>
                                        </div>
                                        {combinedChart && combinedChartCount > 1 ? (
                                            Array.from({length: combinedChartCount}, (_, i) => {
                                                const chartDefault = chartGroupMaxValues[i] || globalMaxValue;
                                                return (
                                                <div key={i} className="p-inputgroup" style={{marginBottom: '4px'}}>
                                                    <span className="p-inputgroup-addon" style={{fontSize: '11px', minWidth: '56px'}}>Chart {i + 1}</span>
                                                    <InputNumber
                                                        value={chartMaxValues[i] ?? chartDefault}
                                                        min={0}
                                                        maxFractionDigits={2}
                                                        onChange={(e) => { if (e.value != null) handleChartMaxChange(i, e.value); }}
                                                    />
                                                    <Button
                                                        icon="pi pi-refresh"
                                                        tooltip={`Reset to chart max (${Math.round(chartDefault * 100) / 100})`}
                                                        tooltipOptions={{position: 'top'}}
                                                        onClick={() => handleChartMaxChange(i, null)}
                                                        className="p-button-secondary"
                                                    />
                                                </div>
                                                );
                                            })
                                        ) : (
                                            <div className="p-inputgroup">
                                                <InputNumber
                                                    value={xAxisMax}
                                                    min={0}
                                                    maxFractionDigits={2}
                                                    onChange={(e) => { if (e.value != null) setXAxisMax(e.value); }}
                                                />
                                                <Button
                                                    icon="pi pi-refresh"
                                                    tooltip={`Reset to max (${globalMaxValue})`}
                                                    tooltipOptions={{position: 'top'}}
                                                    onClick={() => setXAxisMax(globalMaxValue)}
                                                    className="p-button-secondary"
                                                />
                                            </div>
                                        )}
                                        {globalMaxInfo.elementName && (
                                            <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', fontSize: '12px'}}>
                                                <span style={{
                                                    display: 'inline-block',
                                                    width: '12px',
                                                    height: '12px',
                                                    borderRadius: '2px',
                                                    backgroundColor: globalMaxInfo.elementColor ?? '#888',
                                                    flexShrink: 0,
                                                }}/>
                                                <span>{globalMaxInfo.elementName}: {Math.round(globalMaxInfo.value * 100) / 100}{unitSuffix(globalMaxInfo.elementUnit)} @ {Math.round(globalMaxInfo.depth ?? 0)}m</span>
                                            </div>
                                        )}
                                    </div>
                                </>}/>
                </div>
                <div style={{flex: '1 1 0', display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0}}>
                    <div ref={contentDivRef} style={{
                        flex: '1 1 0',
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: layoutDirection === 'horizontal' ? 'row' : 'column',
                        overflowY: layoutDirection === 'vertical' && selectedDrillholes.length > 3 ? 'auto' : 'hidden',
                        overflowX: layoutDirection === 'horizontal' ? 'auto' : 'hidden',
                    }}>
                        {selectedDrillholes.map((dh: any, dhIdx: number) => {
                            const noCharts = dataSources.length === 0 || elements.length === 0;
                            // Depth scale: shown when no charts are visible.
                            // Horizontal layout: only the first row gets the scale (all are synced).
                            // Vertical layout: every row gets its own scale.
                            const showScale = noCharts && (layoutDirection === 'vertical' || dhIdx === 0);
                            return (
                                <DrillholeRow key={dh.hole_id} drillhole={dh} projectId={projectId ?? ''} elements={elements}
                                              totalCount={selectedDrillholes.length} syncView={sharedView}
                                              onViewChange={handleViewChange} viewSyncBus={viewSyncBus}
                                              sharedScrollLeft={sharedScrollLeft} onScrollChange={handleScrollChange}
                                              activeLayer={activeLayer}
                                              tileSplitterSizes={tileSplitterSizes}
                                              onTileSplitterResize={handleTileSplitterResize}
                                              onManualSplitterAdjust={handleManualSplitterAdjust}
                                              xAxisMax={xAxisMax}
                                              xAxisMin={xAxisMin}
                                              elementMaxValues={elementMaxValues}
                                              elementMinValues={elementMinValues}
                                              chartMaxValues={chartMaxValues}
                                              chartMinValues={chartMinValues}
                                              chartGroupMaxValues={chartGroupMaxValues}
                                              combinedChartCount={combinedChartCount}
                                              elementChartIndex={elementChartIndex}
                                              layoutDirection={layoutDirection}
                                              combinedChart={combinedChart}
                                              showMarkers={showMarkers}
                                              isDark={isDark}
                                              onTilesGenerated={handleTilesGenerated}
                                              onViewerReady={handleViewerReady}
                                              viewExtents={viewExtents}
                                              dataSources={dataSources}
                                              showDepthScale={showScale}
                                              chartFixedWidth={chartFixedWidth}
                                              titleSize={titleSize}
                                              onExtentsChange={(min, max) => {
                                                  handleExtentsForPdf(min, max);
                                                  handleExtentsChangeGlobal(min, max);
                                              }}/>
                            );
                        })}
                    </div>
                </div>
            </div>
            <Dialog header="Save Template" visible={showSaveDialog} style={{width: '30vw'}}
                    onHide={() => setShowSaveDialog(false)}
                    footer={
                        <div>
                            <Button label="Cancel" icon="pi pi-times" onClick={() => setShowSaveDialog(false)}
                                    className="p-button-text"/>
                            <Button label="Save" icon="pi pi-check" onClick={handleSaveTemplate} autoFocus
                                    disabled={!newTemplateName.trim()}/>
                        </div>
                    }>
                <div className="flex flex-column gap-2">
                    <label htmlFor="templateName">Name</label>
                    <Dropdown id="templateName"
                              value={newTemplateName}
                              options={templates}
                              optionLabel="name"
                              optionValue="name"
                              onChange={(e) => setNewTemplateName(e.value)}
                              editable
                              placeholder="Enter or select a name"
                              className="p-inputtext-sm"
                    />
                </div>
            </Dialog>
        </div>
    );
}
